import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User, Trophy, Clock, MousePointer2, Globe, Lock, Swords, Users, Star, ExternalLink } from 'lucide-react';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Profile, Run, Match, Challenge, League } from '@/types/game';
import { formatTime } from '@/lib/utils';
import { toast } from 'sonner';
import { CreatorStudio } from '@/components/profile/CreatorStudio';
import { RunsList } from '@/components/profile/RunsList';
import { ChallengesList } from '@/components/profile/ChallengesList';
import { LeaguesList } from '@/components/profile/LeaguesList';

interface ProfileStats {
  totalRuns: number;
  bestTime: number | null;
  lowestClicks: number | null;
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { username } = useParams();
  const { user, profile: authProfile, updateProfile, isLoading: isAuthLoading } = useAuth();
  const [viewedProfile, setViewedProfile] = useState<Profile | null>(null);
  const [stats, setStats] = useState<ProfileStats>({ totalRuns: 0, bestTime: null, lowestClicks: null });
  const [runs, setRuns] = useState<(Run & { match?: Match })[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const isOwnProfile = !username || (authProfile && authProfile.username === username);
  const displayProfile = isOwnProfile ? authProfile : viewedProfile;

  useEffect(() => {
    if (!username && !user && !isAuthLoading) {
      navigate('/auth');
      return;
    }

    if (username && username !== authProfile?.username) {
      fetchPublicProfile(username);
    } else if (user) {
      fetchOwnData();
    }
  }, [username, user, authProfile, isAuthLoading]);

  const fetchPublicProfile = async (uname: string) => {
    setIsLoading(true);
    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('username', uname)
        .eq('is_public', true)
        .single();

      if (error || !profileData) {
        toast.error('Profile not found or is private');
        navigate('/');
        return;
      }

      setViewedProfile(profileData as Profile);
      await fetchStats(profileData.id, true);
      await fetchRuns(profileData.id, true);
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOwnData = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      await Promise.all([
        fetchStats(user.id, false),
        fetchRuns(user.id, false),
        fetchChallenges(user.id),
        fetchLeagues(user.id),
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async (userId: string, publicOnly: boolean) => {
    let query = supabase
      .from('runs')
      .select('duration_ms, clicks, completed')
      .eq('user_id', userId)
      .eq('completed', true);

    if (publicOnly) {
      query = query.eq('is_public', true);
    }

    const { data } = await query;

    if (data && data.length > 0) {
      const completedRuns = data.filter(r => r.completed);
      setStats({
        totalRuns: completedRuns.length,
        bestTime: completedRuns.length > 0 ? Math.min(...completedRuns.map(r => r.duration_ms || Infinity)) : null,
        lowestClicks: completedRuns.length > 0 ? Math.min(...completedRuns.map(r => r.clicks || Infinity)) : null,
      });
    }
  };

  const fetchRuns = async (userId: string, publicOnly: boolean) => {
    let query = supabase
      .from('runs')
      .select('*, match:matches(*)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (publicOnly) {
      query = query.eq('is_public', true);
    }

    const { data } = await query;
    if (data) {
      setRuns(data.map(r => ({ ...r, path: r.path as string[] || [] })) as (Run & { match?: Match })[]);
    }
  };

  const fetchChallenges = async (userId: string) => {
    const { data } = await supabase
      .from('challenges')
      .select('*, from_user:profiles!challenges_from_user_id_fkey(*), match:matches(*)')
      .or(`from_user_id.eq.${userId},to_user_id.eq.${userId}`)
      .order('created_at', { ascending: false })
      .limit(20);

    if (data) {
      setChallenges(data as Challenge[]);
    }
  };

  const fetchLeagues = async (userId: string) => {
    const { data } = await supabase
      .from('league_members')
      .select('league:leagues(*)')
      .eq('user_id', userId);

    if (data) {
      setLeagues(data.map(d => d.league).filter(Boolean) as League[]);
    }
  };

  const toggleRunPublic = async (runId: string, isPublic: boolean) => {
    const { error } = await supabase
      .from('runs')
      .update({ is_public: isPublic })
      .eq('id', runId);

    if (error) {
      toast.error('Failed to update run visibility');
    } else {
      toast.success(isPublic ? 'Run is now public' : 'Run is now private');
      setRuns(runs.map(r => r.id === runId ? { ...r, is_public: isPublic } : r));
    }
  };

  const handleBeatRun = (run: Run) => {
    navigate(`/play?beat=${run.id}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-16 text-center">
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!displayProfile) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-16 text-center">
          <p className="text-muted-foreground">Profile not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Profile Header */}
          <div className="game-card mb-8">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                {displayProfile.avatar_url ? (
                  <img src={displayProfile.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User className="w-8 h-8 text-primary" />
                )}
              </div>
              <div className="flex-1 text-center sm:text-left">
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <h1 className="text-xl font-semibold">{displayProfile.display_name || displayProfile.username}</h1>
                  {displayProfile.is_verified && (
                    <Star className="w-4 h-4 text-accent fill-accent" />
                  )}
                </div>
                <div className="flex items-center gap-2 justify-center sm:justify-start mt-1">
                  <span className="text-muted-foreground text-sm">@{displayProfile.username}</span>
                  {displayProfile.is_public ? (
                    <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                  ) : (
                    <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                  )}
                </div>
              </div>
              {isOwnProfile && (
                <Button variant="outline" size="sm" onClick={() => navigate('/settings')}>
                  Settings
                </Button>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mt-6">
              <div className="stat-block">
                <p className="stat-value text-primary">{stats.totalRuns}</p>
                <p className="stat-label">Runs</p>
              </div>
              <div className="stat-block">
                <p className="stat-value text-accent">{stats.bestTime ? formatTime(stats.bestTime) : '-'}</p>
                <p className="stat-label">Best Time</p>
              </div>
              <div className="stat-block">
                <p className="stat-value text-primary">{stats.lowestClicks ?? '-'}</p>
                <p className="stat-label">Min Clicks</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="runs">
            <TabsList className="w-full justify-start mb-6 bg-secondary/40">
              <TabsTrigger value="runs" className="gap-2">
                <Trophy className="w-4 h-4" />
                Runs
              </TabsTrigger>
              {isOwnProfile && (
                <>
                  <TabsTrigger value="challenges" className="gap-2">
                    <Swords className="w-4 h-4" />
                    Challenges
                  </TabsTrigger>
                  <TabsTrigger value="leagues" className="gap-2">
                    <Users className="w-4 h-4" />
                    Leagues
                  </TabsTrigger>
                  {displayProfile.is_verified && (
                    <TabsTrigger value="creator" className="gap-2">
                      <Star className="w-4 h-4" />
                      Creator
                    </TabsTrigger>
                  )}
                </>
              )}
            </TabsList>

            <TabsContent value="runs">
              <RunsList
                runs={runs}
                isOwnProfile={isOwnProfile}
                onTogglePublic={toggleRunPublic}
                onBeatRun={handleBeatRun}
              />
            </TabsContent>

            {isOwnProfile && (
              <>
                <TabsContent value="challenges">
                  <ChallengesList challenges={challenges} userId={user?.id || ''} />
                </TabsContent>
                <TabsContent value="leagues">
                  <LeaguesList leagues={leagues} />
                </TabsContent>
                {displayProfile.is_verified && (
                  <TabsContent value="creator">
                    <CreatorStudio profile={displayProfile} />
                  </TabsContent>
                )}
              </>
            )}
          </Tabs>
        </div>
      </main>
    </div>
  );
}
